"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useLocale, useTranslations } from "next-intl";
import { CaretDown } from "@phosphor-icons/react/dist/ssr";
import { setLocaleClient, isLocale } from "@/lib/locale";
import { routing, stripLocalePrefix } from "@/i18n/routing";
import { useReducedMotion } from "@/lib/hooks/use-reduced-motion";

type Language = { code: string; label: string; short: string } & (
  | { flagCode: string; glyph?: never }
  | { glyph: string; flagCode?: never }
);

/**
 * The Chinese pair wears a GLYPH, not a flag (owner, 2026-09-23: 简 for
 * Simplified, 繁 for Traditional). Script is the axis, not country: one
 * Traditional file serves Taiwan and Hong Kong alike, and any flag chosen for
 * it would be a political statement (i18n-expansion-spec §9.3).
 */
const LANGUAGES: readonly Language[] = [
  { code: "en",      label: "English",    short: "EN",   flagCode: "gb" },
  { code: "vi",      label: "Tiếng Việt", short: "VI",   flagCode: "vn" },
  { code: "fr",      label: "Français",   short: "FR",   flagCode: "fr" },
  { code: "zh-Hans", label: "简体中文",    short: "简体", glyph: "简" },
  { code: "zh-Hant", label: "繁體中文",    short: "繁體", glyph: "繁" },
];

/**
 * The round mark before a language: its flag, or its glyph, gold on navy
 * with the flags' outline. 16px in the trigger and the segmented control, 18px
 * in the dropdown list (owner, 2026-09-25: "a bit" larger than 14 / 16).
 */
function LanguageMark({ lang, size }: { lang: Language; size: 16 | 18 }) {
  const box = size === 16 ? "h-16 w-16" : "h-18 w-18";
  if (lang.glyph) {
    // ⛔ SVG with dominant-baseline "central", NOT a flex-centred text span.
    // Measured 2026-09-23 (ink bounds of the real render, 10x): as text the
    // glyph sat 0.7px LOW in the 14px circle (ink centre 7.70 against 7.0),
    // with Inter or with a CJK-only stack alike, because PingFang draws its
    // ink below the middle of its own line box and flex centres the box.
    // `text-box: trim-both ideographic` is unsupported, and a fixed nudge is
    // only right for one OS's font. "central" centres on the font's own
    // ideographic centre: 6.90 to 6.95 with PingFang and Hiragino, within
    // 0.35px with the others tried.
    // Size 9.5 of the 14-unit circle, semibold (owner, 2026-09-25: at 11 and
    // bold the character looked cramped once the circles grew). In the 16px
    // circle that is about 11px, the size it had in the old 14px circle, now
    // with room around it. 9 in a 14px circle was too small (2026-09-23).
    // Gold on navy (owner, 2026-09-25, after trying the admin count badge's
    // pale gold, which faded into the mobile menu's grey control). Keeps the
    // flags' faint outline so every mark has one footprint.
    return (
      <svg aria-hidden="true" viewBox="0 0 14 14" className={`${box} shrink-0 rounded-full ring-1 ring-black/10`}>
        <circle cx="7" cy="7" r="7" className="fill-navy" />
        <text x="7" y="7" textAnchor="middle" dominantBaseline="central" fontSize="9.5" fontWeight={600} className="fill-gold">
          {lang.glyph}
        </text>
      </svg>
    );
  }
  return (
    <span aria-hidden="true" className={`inline-flex ${box} shrink-0 overflow-hidden rounded-full ring-1 ring-black/10`}>
      {/* eslint-disable-next-line @next/next/no-img-element -- inline SVG flag; next/image's overhead + DOM wrapper hurts vs helps for a 16-18px icon */}
      <img src={`/flags/${lang.flagCode}.svg`} alt="" className="h-full w-full object-cover" />
    </span>
  );
}

/**
 * `variant` changes only the presentation — every locale rule below (cookie
 * sync, prefix computation, the window-derived path that keeps this
 * static-render-safe) is shared. "segmented" is the mobile menu's inline
 * EN/VI control, which has no label beside it and so must read as a switcher
 * on its own.
 */
export default function LanguageSwitcher({ variant = "dropdown", labelledBy }: {
  variant?: "dropdown" | "segmented";
  /** id of a VISIBLE label naming the segmented group (the mobile menu's
   *  "Language"); without one the group keeps its own aria-label. */
  labelledBy?: string;
} = {}) {
  // The URL locale (from NextIntlClientProvider context) is the source of
  // truth — static-safe, unlike usePathname()/useSearchParams() which would
  // make the static Nav shell dynamic on [slug] routes.
  const current = useLocale();
  const t = useTranslations("nav");
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLUListElement>(null);
  const reducedMotion = useReducedMotion();

  // Close on outside click
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  // Close on Escape
  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        setOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [open]);

  // Focus current item when menu opens
  useEffect(() => {
    if (!open) return;
    const buttons = menuRef.current?.querySelectorAll<HTMLElement>("button");
    if (!buttons) return;
    const idx = LANGUAGES.findIndex((l) => l.code === current);
    buttons[idx >= 0 ? idx : 0]?.focus();
  }, [open, current]);

  const select = (code: string) => {
    if (!isLocale(code)) return;
    setOpen(false);
    if (code === current) return;
    // Sync both locale cookies (et_lang for data labels + NEXT_LOCALE for the
    // URL-locale middleware) — see setLocaleClient. Syncing NEXT_LOCALE is what
    // stops the default locale being bounced back by a stale cookie ("stuck on
    // VI"). Then navigate to the locale-prefixed URL (default = unprefixed),
    // computed from window.location at click time so we avoid usePathname() and
    // keep this static-render-safe in the Nav shell on [slug] routes.
    setLocaleClient(code);
    const stripped = stripLocalePrefix(window.location.pathname);
    const prefix = code === routing.defaultLocale ? "" : `/${code}`;
    const target = `${prefix}${stripped}` || "/";
    // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- a full load is the point: the locale cookies must reach the server, and useRouter would make the static Nav shell dynamic.
    window.location.assign(`${target}${window.location.search}${window.location.hash}`);
  };

  // Arrow key navigation within the menu
  const handleMenuKeyDown = useCallback((e: React.KeyboardEvent) => {
    const buttons = menuRef.current?.querySelectorAll<HTMLElement>("button");
    if (!buttons || buttons.length === 0) return;

    const items = Array.from(buttons);
    const idx = items.indexOf(document.activeElement as HTMLElement);

    switch (e.key) {
      case "ArrowDown": {
        e.preventDefault();
        const next = idx < items.length - 1 ? idx + 1 : 0;
        items[next].focus();
        break;
      }
      case "ArrowUp": {
        e.preventDefault();
        const prev = idx > 0 ? idx - 1 : items.length - 1;
        items[prev].focus();
        break;
      }
      case "Home": {
        e.preventDefault();
        items[0].focus();
        break;
      }
      case "End": {
        e.preventDefault();
        items[items.length - 1].focus();
        break;
      }
    }
  }, []);

  // Fall back to the default (EN) entry when the active URL locale isn't in the
  // visible list (a routed locale not yet offered). Its content falls back to
  // EN, so showing EN is consistent, and it avoids an `undefined.label` crash
  // during that locale's prerender.
  const active = LANGUAGES.find((l) => l.code === current) ?? LANGUAGES[0];

  if (variant === "segmented") {
    return (
      <div
        role="group"
        {...(labelledBy ? { "aria-labelledby": labelledBy } : { "aria-label": t("selectLanguage") })}
        className="flex flex-1 gap-2 rounded-control bg-navy/[0.06] p-3"
      >
        {LANGUAGES.map((lang) => {
          const isActive = lang.code === current;
          return (
            <button
              key={lang.code}
              type="button"
              onClick={() => select(lang.code)}
              aria-pressed={isActive}
              className={[
                // One Fibonacci step tighter below `xs` (375px) only: five
                // languages (with the 简/繁 pair) need 302px, which fits a
                // 375px phone with the full spacing but clipped the last button
                // on a 320px one (review, 2026-09-23).
                "flex flex-1 items-center justify-center gap-5 xs:gap-8 whitespace-nowrap rounded-md px-5 xs:px-8 py-5 text-xs font-semibold uppercase tracking-wider transition-colors",
                "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold",
                isActive ? "bg-white text-navy shadow-e1" : "text-gray-500 hover:text-navy",
              ].join(" ")}
            >
              <LanguageMark lang={lang} size={16} />
              {lang.short}
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div ref={containerRef} className="relative">
      {/* Trigger */}
      <button
        ref={triggerRef}
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        aria-label={t("languageLabel", { label: active.label })}
        className="inline-flex items-center gap-5 rounded-control border border-navy/15 px-13 py-8 text-sm font-semibold uppercase tracking-[0.06em] text-navy transition-colors hover:border-navy/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold"
      >
        <LanguageMark lang={active} size={16} />
        {active.short}
        <CaretDown
          size={9}
          weight="bold"
          aria-hidden="true"
          className={`transition-transform duration-200 ${open ? "rotate-180" : ""}`}
        />
      </button>

      {/* Dropdown */}
      <ul
          ref={menuRef}
          role="menu"
          aria-label={t("selectLanguage")}
          aria-hidden={!open}
          onKeyDown={handleMenuKeyDown}
          className="absolute right-0 top-full z-chrome mt-8 w-[168px] rounded-pop border border-card-line bg-white p-8 shadow-e4"
          style={{
            opacity: open ? 1 : 0,
            transform: open ? "translateY(0)" : "translateY(-6px)",
            pointerEvents: open ? "auto" : "none",
            transition: reducedMotion ? "none" : "opacity 160ms ease, transform 160ms ease",
          }}
        >
          {LANGUAGES.map((lang) => {
            const isActive = lang.code === current;
            return (
              <li key={lang.code} role="menuitemradio" aria-checked={isActive}>
                <button
                  onClick={() => select(lang.code)}
                  tabIndex={open ? 0 : -1}
                  className={[
                    "flex w-full items-center gap-13 rounded-control px-13 py-[10px] text-sm transition-colors hover:bg-surface",
                    isActive
                      ? "font-semibold text-navy"
                      : "font-normal text-gray-500",
                  ].join(" ")}
                >
                  <LanguageMark lang={lang} size={18} />
                  <span className="flex-1 text-left">{lang.label}</span>
                  {isActive && (
                    <span aria-hidden="true" className="h-[6px] w-[6px] rounded-full bg-gold" />
                  )}
                </button>
              </li>
            );
          })}
        </ul>
    </div>
  );
}
